import React, { useState, useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

const MineralTabs = ({ data }) => {
  const [activeTab, setActiveTab] = useState(0);
  const sectionRef = useRef(null);
  const leftSideRef = useRef(null);
  const contentRef = useRef(null);
  const imageWrapperRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);

const contentAreaRef = useRef(null);

  const currentTab = activeTab !== null ? data[activeTab] : null;

  useEffect(() => {
    let ctx = gsap.context(() => {
      ScrollTrigger.matchMedia({
        // 1. DESKTOP ONLY (> 1400px): Pinning enabled with 2-column card layout
        "(min-width: 1401px)": function () {
          // Sidebar Pinning
          ScrollTrigger.create({
            trigger: sectionRef.current,
            start: "top top+=20",
            end: () => {
              if (!sectionRef.current || !leftSideRef.current) return "+=0";
              const sectionHeight = sectionRef.current.offsetHeight;
              const leftHeight = leftSideRef.current.offsetHeight;
              return `+=${Math.max(0, sectionHeight - leftHeight - 150)}`;
            },
            pin: leftSideRef.current,
            pinSpacing: false,
            invalidateOnRefresh: true,
          });

// Image Pinning — only when content is taller than the image
if (
  imageLoaded &&
  contentAreaRef.current &&
  imageWrapperRef.current
) {
  const contentHeight = contentAreaRef.current.offsetHeight;
  const imageHeight = imageWrapperRef.current.offsetHeight;

  // Pin the image only when the content is taller
  if (contentHeight > imageHeight) {
    ScrollTrigger.create({
      trigger: imageWrapperRef.current,
      start: "top top+=100",
      endTrigger: contentRef.current,
      end: "bottom bottom-=100",
      pin: true,
      pinReparent: true,
      anticipatePin: 1,
      invalidateOnRefresh: true,
    });
  }
}
        },

        // 2. LAPTOP / MEDIUM SCREENS (769px to 1400px): Sidebar pins, but card switches to full-width stack
        "(min-width: 769px) and (max-width: 1400px)": function () {
          ScrollTrigger.create({
            trigger: sectionRef.current,
            start: "top top+=20",
            end: () => {
              if (!sectionRef.current || !leftSideRef.current) return "+=0";
              const sectionHeight = sectionRef.current.offsetHeight;
              const leftHeight = leftSideRef.current.offsetHeight;
              return `+=${Math.max(0, sectionHeight - leftHeight - 40)}`;
            },
            pin: leftSideRef.current,
            pinSpacing: false,
            invalidateOnRefresh: true,
          });
        },
      });
    }, sectionRef);

    return () => ctx.revert();
}, [activeTab, imageLoaded]);

  const handleTabClick = (index) => {
    setImageLoaded(false);
    if (window.innerWidth <= 768) {
      setActiveTab(activeTab === index ? null : index);
      return;
    }

    setActiveTab(index);

    if (sectionRef.current) {
      const sectionTop =
        sectionRef.current.getBoundingClientRect().top + window.scrollY;

      if (window.scrollY > sectionTop + 80) {
        gsap.to(window, {
          duration: 0.4,
          scrollTo: { y: sectionTop, autoKill: false },
          ease: "power2.out",
        });
      }
    }
  };

  useEffect(() => {
    if (window.innerWidth > 768 && contentRef.current) {
      gsap.fromTo(
        contentRef.current,
        { opacity: 0, y: 15 },
        { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" }
      );

      const timer = setTimeout(() => {
        ScrollTrigger.refresh();
      }, 150);

      return () => clearTimeout(timer);
    }
  }, [activeTab]);

  const getText = (node) => {
    if (typeof node === "string") return node;
    if (Array.isArray(node)) return node.map(getText).join("");
    if (React.isValidElement(node)) return getText(node.props.children);
    return "";
  };

  return (
    <section className="mineral-tabs-section container" ref={sectionRef}>
      {/* LEFT SIDEBAR */}
      <div className="mineral-tabs-left" ref={leftSideRef}>
        {data.map((tab, index) => (
          <React.Fragment key={index}>
            <button
              className={`mineral-tab-btn ${
                activeTab === index ? "active" : ""
              }`}
              onClick={() => handleTabClick(index)}
            >
              <h3 className="section-subheading">{tab.tabTitle}</h3>
            </button>

            {/* MOBILE ACCORDION CONTENT */}
            {activeTab === index && (
              <div className="mobile-tab-content">
                <div className="mobile-tab-row">
                  <div className="mobile-tab-image-wrapper">
                    <img
                      src={tab.image}
                      alt={`${getText(tab.heading)} | Iris Impulse India Pvt. Ltd.`}
                      className="mobile-tab-image"
                    />
                  </div>
                  <div className="mobile-tab-content-area">
                    <h2 className="section-heading">{tab.heading}</h2>
                    {tab.description && (
                      <p className="section-text">{tab.description}</p>
                    )}

                    {tab.specialFeaturePoints?.length > 0 && (
                      <div className="mineral-tabs-feature-list">
                        {tab.specialFeaturePoints.map((item, idx) => (
                          <div className="mineral-tabs-feature-item" key={idx}>
                            <h4 className="card-heading">{item.title}</h4>
                            <p className="section-text">{item.desc}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {tab.points?.length > 0 && (
                      <ul className="mineral-tabs-points mobile-tab-points">
                        {tab.points.map((point, idx) => (
                          <li
                            key={idx}
                            className="section-text"
                            dangerouslySetInnerHTML={{ __html: point }}
                          />
                        ))}
                      </ul>
                    )}

                    {tab.featurePoints?.length > 0 && (
                      <div className="mineral-tabs-feature-list">
                        {tab.featurePoints.map((item, idx) => (
                          <div className="mineral-tabs-feature-item" key={idx}>
                            <h4 className="card-heading">{item.title}</h4>
                            <p className="section-text">{item.desc}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {tab.bottomPoints?.length > 0 && (
                      <ul className="mineral-tabs-points mobile-tab-points">
                        {tab.bottomPoints.map((point, idx) => (
                          <li key={idx} className="section-text">
                            {point}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* RIGHT SIDE CONTENT */}
      {currentTab && (
        <div className="mineral-tabs-right">
          <div className="mineral-tabs-card" ref={contentRef}>
            <h2 className="section-heading">{currentTab.heading}</h2>

            <div className="mineral-tabs-row">
              {/* IMAGE WRAPPER */}
              <div
                className="mineral-tabs-image-wrapper"
                ref={imageWrapperRef}
              >
                <img
                  src={currentTab.image}
                  alt={`${getText(currentTab.heading)} | Iris Impulse India Pvt. Ltd.`}
                  className="mineral-tabs-image"
                 onLoad={() => {
  setImageLoaded(true);
  ScrollTrigger.refresh();
}}
                />
              </div>

              {/* CONTENT AREA */}
              <div className="mineral-tabs-content" ref={contentAreaRef}>
                {currentTab.description && (
                  <p className="section-text">{currentTab.description}</p>
                )}

                {currentTab.specialFeaturePoints?.length > 0 && (
                  <div className="mineral-tabs-feature-list">
                    {currentTab.specialFeaturePoints.map((item, idx) => (
                      <div className="mineral-tabs-feature-item" key={idx}>
                        <h4 className="card-heading">{item.title}</h4>
                        <p className="section-text">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                )}

                {currentTab.points?.length > 0 && (
                  <ul className="mineral-tabs-points">
                    {currentTab.points.map((point, idx) => (
                      <li
                        key={idx}
                        className="section-text"
                        dangerouslySetInnerHTML={{ __html: point }}
                      />
                    ))}
                  </ul>
                )}

                {currentTab.featurePoints?.length > 0 && (
                  <div className="mineral-tabs-feature-list">
                    {currentTab.featurePoints.map((item, idx) => (
                      <div className="mineral-tabs-feature-item" key={idx}>
                        <h4 className="card-heading">{item.title}</h4>
                        <p className="section-text">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                )}

                {currentTab.bottomPoints?.length > 0 && (
                  <ul className="mineral-tabs-points">
                    {currentTab.bottomPoints.map((point, idx) => (
                      <li key={idx} className="section-text">
                        {point}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default MineralTabs;